import { useState } from "react";

/**
 * ArticleCover — renders a hero-style cover image for an article.
 *
 * Two visual modes:
 *
 *   1. Real photo (preferred) — when `imageUrl` is provided (admin-set
 *      `coverUrl` field, or a vision_expert default seeded into the
 *      DB), display a 16:9 photographic cover with subtle border and
 *      lazy loading. If the image fails to load (404 / blocked), we
 *      automatically fall back to mode 2.
 *
 *   2. SVG geometry on accent-soft background — the original minimal
 *      on-brand cover. Variants give different article archetypes a
 *      slightly different feel without resorting to stock graphics.
 *
 * Accessibility:
 *   - The cover is decorative when no `alt` text is supplied (default).
 *     Pages that want the image announced (e.g. article detail page)
 *     can pass an explicit `alt` prop derived from the article title.
 */
export default function ArticleCover({
  variant = "default",
  imageUrl = "",
  alt = "",
  className = "",
}) {
  const trimmed = (imageUrl || "").trim();
  const [errored, setErrored] = useState(false);
  const useImage = !!trimmed && !errored;

  if (useImage) {
    return (
      <div
        className={`relative w-full aspect-[16/9] rounded-xl overflow-hidden border border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)] ${className}`}
        data-testid="article-cover-image"
      >
        <img
          src={trimmed}
          alt={alt || ""}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setErrored(true)}
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden={alt ? undefined : true}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative w-full aspect-[16/9] rounded-xl overflow-hidden border border-[color:var(--sas-line)] ${className}`}
      style={{ background: "var(--sas-accent-soft)" }}
      aria-hidden
      data-testid="article-cover-svg"
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 400 225"
        preserveAspectRatio="xMidYMid slice"
      >
        {variant === "research" && (
          <g stroke="currentColor" fill="none" style={{ color: "var(--sas-accent)" }} strokeWidth="0.8" opacity="0.55">
            <circle cx="300" cy="112" r="70" />
            <circle cx="300" cy="112" r="48" />
            <circle cx="300" cy="112" r="26" />
            <line x1="30" y1="60" x2="180" y2="60" />
            <line x1="30" y1="95" x2="160" y2="95" />
            <line x1="30" y1="130" x2="170" y2="130" />
            <line x1="30" y1="165" x2="140" y2="165" />
          </g>
        )}
        {variant === "presentations" && (
          <g stroke="currentColor" fill="none" style={{ color: "var(--sas-accent)" }} strokeWidth="0.8" opacity="0.55">
            <rect x="60" y="50" width="280" height="125" rx="6" />
            <rect x="80" y="70" width="110" height="12" rx="2" />
            <rect x="80" y="92" width="170" height="6" rx="2" />
            <rect x="80" y="106" width="150" height="6" rx="2" />
            <rect x="80" y="130" width="60" height="40" rx="3" />
            <rect x="150" y="130" width="60" height="40" rx="3" />
            <rect x="220" y="130" width="60" height="40" rx="3" />
          </g>
        )}
        {variant === "default" && (
          <g stroke="currentColor" fill="none" style={{ color: "var(--sas-accent)" }} strokeWidth="0.8" opacity="0.5">
            <line x1="0" y1="56" x2="400" y2="56" />
            <line x1="0" y1="112" x2="400" y2="112" />
            <line x1="0" y1="168" x2="400" y2="168" />
            <circle cx="200" cy="112" r="54" />
          </g>
        )}
      </svg>
    </div>
  );
}
